import { TMessage } from "@/i18n/tt";
import {
  CustomFieldDataType,
  CustomFieldImportProblem,
  importProblemLabels,
} from "./customFieldsDomain";

export type SpreadsheetImportRow = {
  row: number;
  countryCode: string;
  orgNo: string;
  cells: string[];
};

export type SpreadsheetImportIssue = {
  problem: CustomFieldImportProblem;
  row?: number;
  column?: number;
  value?: string;
  companyName?: string;
};

export type CellEditor =
  | { type: "text" }
  | { type: "select"; options: string[] }
  | { type: "country" }
  | { type: "date" };

export type ImportFieldOption = {
  key: string;
  label: TMessage;
  section: TMessage;
  editor: CellEditor;
  tag?: TMessage;
};

export type SpreadsheetImportGrid = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  columns: (string | undefined)[];
  fieldsByKey: Map<string, ImportFieldOption>;
  errorsOnly: boolean;
  selectedOnly: boolean;
};

type SpreadsheetImportEdits = {
  rows: SpreadsheetImportRow[];
  columns: (string | undefined)[];
};

const spreadsheetRejections = [
  "UNREADABLE_FILE",
  "NO_ROWS",
  "TOO_MANY_ROWS",
  "TOO_MANY_COLUMNS",
  "TOO_LARGE",
] as const;

type SpreadsheetRejection = (typeof spreadsheetRejections)[number];

const isSpreadsheetRejection = (value: unknown): value is SpreadsheetRejection =>
  spreadsheetRejections.some((rejection) => rejection === value);

const spreadsheetRejectionMessages = (
  maxRows?: number,
): Record<SpreadsheetRejection, TMessage> => ({
  UNREADABLE_FILE: { key: "entity-data.spreadsheet-rejection-UNREADABLE_FILE" },
  NO_ROWS: { key: "entity-data.spreadsheet-rejection-NO_ROWS" },
  TOO_MANY_ROWS: {
    key: "entity-data.spreadsheet-rejection-TOO_MANY_ROWS",
    params: { max: { type: "number", value: maxRows ?? 0 } },
  },
  TOO_MANY_COLUMNS: { key: "entity-data.spreadsheet-rejection-TOO_MANY_COLUMNS" },
  TOO_LARGE: { key: "entity-data.spreadsheet-rejection-TOO_LARGE" },
});

export const spreadsheetRejectionMessage = (err: unknown): TMessage => {
  const data = (err as { response?: { data?: { rejection?: string; maxRows?: number } } })?.response
    ?.data;
  const rejection = data?.rejection;
  return isSpreadsheetRejection(rejection)
    ? spreadsheetRejectionMessages(data?.maxRows)[rejection]
    : { key: "entity-data.upload-error" };
};

export const cellEditor = (dataType: CustomFieldDataType, options: string[] = []): CellEditor => {
  switch (dataType) {
    case "SINGLE_SELECT":
      return { type: "select", options };
    case "COUNTRY_CODE":
      return { type: "country" };
    case "DATE":
      return { type: "date" };
    case "TEXT":
    case "NUMBER":
    case "CONTACT":
      return { type: "text" };
  }
};

type IssueIndex = {
  rows: Map<number, SpreadsheetImportIssue>;
  cells: Map<string, SpreadsheetImportIssue>;
  columns: Map<number, SpreadsheetImportIssue>;
  rowsWithIssues: Set<number>;
};

const cellKey = (row: number, column: number) => `${row}:${column}`;

const issueIndexes = new WeakMap<SpreadsheetImportIssue[], IssueIndex>();

export const indexIssues = (issues: SpreadsheetImportIssue[]): IssueIndex => {
  const cached = issueIndexes.get(issues);
  if (cached) {
    return cached;
  }
  const index = buildIssueIndex(issues);
  issueIndexes.set(issues, index);
  return index;
};

const buildIssueIndex = (issues: SpreadsheetImportIssue[]): IssueIndex => ({
  rows: new Map(
    issues.flatMap((issue) =>
      issue.row !== undefined && issue.column === undefined ? [[issue.row, issue] as const] : [],
    ),
  ),
  cells: new Map(
    issues.flatMap((issue) =>
      issue.row !== undefined && issue.column !== undefined
        ? [[cellKey(issue.row, issue.column), issue] as const]
        : [],
    ),
  ),
  columns: new Map(
    issues.flatMap((issue) =>
      issue.row === undefined && issue.column !== undefined ? [[issue.column, issue] as const] : [],
    ),
  ),
  rowsWithIssues: new Set(issues.flatMap((issue) => issue.row ?? [])),
});

export type ImportGridColumn = {
  index: number;
  header: string;
  selectedKey?: string;
  field?: ImportFieldOption;
  issue?: TMessage;
};

export type ImportGridCell = {
  column: number;
  text: string;
  editor?: CellEditor;
  issue?: TMessage;
};

export type ImportGridRow = {
  row: number;
  countryCode: string;
  orgNo: string;
  issue?: TMessage;
  cells: ImportGridCell[];
};

const sameEditor = (a: CellEditor | undefined, b: CellEditor | undefined): boolean =>
  a?.type === b?.type &&
  (a?.type !== "select" || (b?.type === "select" && a.options === b.options));

export const sameGridCell = (a: ImportGridCell, b: ImportGridCell): boolean =>
  a.column === b.column &&
  a.text === b.text &&
  a.issue === b.issue &&
  sameEditor(a.editor, b.editor);

export type ImportResultRow = {
  id: string;
  row?: number;
  orgNo?: string;
  companyName?: string;
  column?: string;
  field?: TMessage;
  problem: TMessage;
  value?: string;
};

type ImportResultCount = {
  key: string;
  label: TMessage;
  value: number;
};

export type SpreadsheetImportResult = {
  counts: ImportResultCount[];
  nothingImported: boolean;
  rows: ImportResultRow[];
};

const problemLabel = (issue: SpreadsheetImportIssue): TMessage =>
  importProblemLabels[issue.problem];

export const gridColumns = (grid: SpreadsheetImportGrid, index: IssueIndex): ImportGridColumn[] =>
  grid.headers
    .map((header, column) => {
      const issue = index.columns.get(column);
      return {
        index: column,
        header,
        selectedKey: grid.columns[column],
        field: grid.fieldsByKey.get(grid.columns[column] ?? ""),
        issue: issue && problemLabel(issue),
      };
    })
    .filter((column) => !grid.selectedOnly || column.selectedKey !== undefined);

export type ImportGridRows = {
  count: number;
  rowNumberAt: (position: number) => number;
  at: (position: number) => ImportGridRow;
};

export const gridRowSource = (
  grid: SpreadsheetImportGrid,
  index: IssueIndex,
  columns: ImportGridColumn[],
): ImportGridRows => {
  const rows = grid.errorsOnly
    ? grid.rows.filter((row) => index.rowsWithIssues.has(row.row))
    : grid.rows;
  const toGridRow = (row: SpreadsheetImportRow): ImportGridRow => {
    const rowIssue = index.rows.get(row.row);
    return {
      row: row.row,
      countryCode: row.countryCode,
      orgNo: row.orgNo,
      issue: rowIssue && problemLabel(rowIssue),
      cells: columns.map((column) => {
        const issue = index.cells.get(cellKey(row.row, column.index));
        return {
          column: column.index,
          text: row.cells[column.index] ?? "",
          editor: column.field?.editor,
          issue: issue && problemLabel(issue),
        };
      }),
    };
  };
  return {
    count: rows.length,
    rowNumberAt: (position) => rows[position].row,
    at: (position) => toGridRow(rows[position]),
  };
};

export const sameGridRow = (a: ImportGridRow, b: ImportGridRow): boolean =>
  a.row === b.row &&
  a.countryCode === b.countryCode &&
  a.orgNo === b.orgNo &&
  a.issue === b.issue &&
  a.cells.length === b.cells.length &&
  a.cells.every((cell, position) => sameGridCell(cell, b.cells[position]));

export const resultRows = (
  grid: SpreadsheetImportGrid,
  issues: SpreadsheetImportIssue[],
): ImportResultRow[] => {
  const orgNos = new Map(grid.rows.map((row) => [row.row, row.orgNo]));
  return issues.map((issue, position) => {
    const key = issue.column === undefined ? undefined : grid.columns[issue.column];
    const field = key === undefined ? undefined : grid.fieldsByKey.get(key);
    return {
      id: `${position}`,
      row: issue.row,
      orgNo: issue.row === undefined ? undefined : orgNos.get(issue.row),
      companyName: issue.companyName,
      column: issue.column === undefined ? undefined : grid.headers[issue.column],
      field: field?.label,
      problem: problemLabel(issue),
      value: issue.value,
    };
  });
};

export const tabSeparated = (rows: string[][]): string =>
  rows.map((cells) => cells.map((cell) => cell.replace(/[\t\r\n]+/g, " ")).join("\t")).join("\n");

export const errorCount = (issues: SpreadsheetImportIssue[]): number =>
  indexIssues(issues).rowsWithIssues.size;

export const takenElsewhere = (
  columns: (string | undefined)[],
): ((key: string, column: number) => boolean) => {
  const taken = new Map(columns.flatMap((key, column) => (key ? [[key, column] as const] : [])));
  return (key, column) => {
    const owner = taken.get(key);
    return owner !== undefined && owner !== column;
  };
};

export const blankUnmappedCells = (
  rows: SpreadsheetImportRow[],
  isMapped: (column: number) => boolean,
): SpreadsheetImportRow[] =>
  rows.map((row) => ({
    ...row,
    cells: row.cells.map((cell, index) => (isMapped(index) ? cell : "")),
  }));

const rowOf = (edits: SpreadsheetImportEdits, rowNumber: number) =>
  edits.rows.find((row) => row.row === rowNumber);

const replaceRow = (
  rows: SpreadsheetImportRow[],
  rowNumber: number,
  change: Partial<SpreadsheetImportRow>,
): SpreadsheetImportRow[] =>
  rows.map((row) => (row.row === rowNumber ? { ...row, ...change } : row));

export const withColumnField = <S extends SpreadsheetImportEdits>(
  state: S,
  column: number,
  key: string | undefined,
): S =>
  state.columns[column] === key
    ? state
    : {
        ...state,
        columns: state.columns.map((current, index) => (index === column ? key : current)),
      };

export const withRow = <S extends SpreadsheetImportEdits>(
  state: S,
  rowNumber: number,
  change: Partial<Pick<SpreadsheetImportRow, "countryCode" | "orgNo">>,
): S => {
  const row = rowOf(state, rowNumber);
  const changes = (Object.keys(change) as (keyof typeof change)[]).some(
    (key) => row?.[key] !== change[key],
  );
  return changes ? { ...state, rows: replaceRow(state.rows, rowNumber, change) } : state;
};

export const withCell = <S extends SpreadsheetImportEdits>(
  state: S,
  rowNumber: number,
  column: number,
  text: string,
): S => {
  const row = rowOf(state, rowNumber);
  return !row || row.cells[column] === text
    ? state
    : {
        ...state,
        rows: replaceRow(state.rows, rowNumber, {
          cells: row.cells.map((cell, index) => (index === column ? text : cell)),
        }),
      };
};

export type SpreadsheetImportGridProps = {
  fieldsByKey: Map<string, ImportFieldOption>;
  countryCodes: string[];
  fieldCountryCodes: string[];
  gridColumns: ImportGridColumn[];
  gridRows: ImportGridRows;
  rowCount: number;
  errorCount: number;
  errorsOnly: boolean;
  selectedOnly: boolean;
  isValidating: boolean;
  isTakenElsewhere: (key: string, column: number) => boolean;
  onSelectField: (column: number, key: string | undefined) => void;
  onChangeCountryCode: (row: number, countryCode: string) => void;
  onChangeOrgNo: (row: number, orgNo: string) => void;
  onChangeCell: (row: number, column: number, text: string) => void;
  onToggleErrorsOnly: () => void;
  onToggleSelectedOnly: () => void;
};
